<template>
<div id="songs-playlist-profile">

    <div class="d-f fd-c ai-c p-lg mute tt-up">
        <p v-if="type === 'label'" class="m-t-lg">
            <router-link v-if="isOwnProfile" to="/settings" class="">Releases</router-link>
            <template v-else>Releases</template>
            <span class="mute">&nbsp;{{ playlist?.songs?.length }}</span>
        </p>
        <p v-else class="d-f ai-c fd-c">
            <div class="d-f">
                <span>Tracks</span>
                <span class="mute">&nbsp;{{ playlist?.songs?.length }}</span>
            </div>
            <router-link v-if="isOwnProfile && !playlist?.songs?.length" to="/my_music" class="text-link mute">Add tracks</router-link>
        </p>
    </div>

    <!-- Carousel: > 2 songs -->
    <template v-if="isCarousel">
        <div class="carousel-outer">
            <div class="carousel" ref="carousel" @scroll.passive="onCarouselScroll">
                <SongBlockProfile
                    v-for="(song, key) in playlist.songs"
                    :song="song"
                    :count="key"
                    :key="song.songUid"
                    :playlist-position="playlistPosition"
                    :editButtons="editButtons"
                    :artist-handle="artistHandle"
                    @play-item="$emit('play-select-song', {playerPosition: playlistPosition, trackId: key})"
                />
            </div>
        </div>

        <!-- Desktop scroll indicator -->
        <div class="scroll-track" v-show="isScrollable">
            <div class="scroll-thumb" :style="{ width: scrollThumbWidth + '%', left: scrollThumbLeft + '%' }" />
        </div>

        <!-- Mobile dots + nav -->
        <div class="dots">
            <!-- <div class="dot-nav" :disabled="activeIndex === 0" @click="scrollToSlide(activeIndex - 1)">
                <IconBase :w="15" :h="15" class="flip-h"><IconChevronRight /></IconBase>
            </div> -->
            <div class="dots-inner">
                <span
                    v-for="(_, i) in playlist.songs"
                    :key="i"
                    class="dot"
                    :class="{ 'dot--active': i === activeIndex }"
                />
            </div>
            <!-- <div class="dot-nav" :disabled="activeIndex === playlist.songs.length - 1" @click="scrollToSlide(activeIndex + 1)">
                <IconBase :w="15" :h="15"><IconChevronRight /></IconBase>
            </div> -->
        </div>
    </template>

    <!-- Stack: ≤ 2 songs -->
    <template v-else>
        <div class="d-f fd-c ai-c p">
            <SongBlockProfile
                v-for="(song, key) in playlist.songs"
                :song="song"
                :count="key"
                :key="song.songUid"
                :playlist-position="playlistPosition"
                :editButtons="editButtons"
                :artist-handle="artistHandle"
                @play-item="$emit('play-select-song', {playerPosition: playlistPosition, trackId: key})"
            />
        </div>
    </template>

</div>
</template>

<script>
import SongBlockProfile from '@/components/profile/SongBlockProfile'
import IconBase from '@/assets/icons/IconBase.vue'
import IconChevronRight from '@/assets/icons/IconChevronRight.vue'

export default {
    name: 'SongsPlaylistProfile',
    components: { SongBlockProfile, IconBase, IconChevronRight },
    props: ['playlist', 'playlistPosition', 'editButtons', 'type', 'isOwnProfile', 'artistHandle'],
    data() {
        return {
            activeIndex: 0,
            scrollThumbWidth: 100,
            scrollThumbLeft: 0,
            isScrollable: false,
        };
    },
    computed: {
        isCarousel() {
            return (this.playlist?.songs?.length || 0) > 2;
        },
    },
    watch: {
        isCarousel(val) {
            if (val) this.$nextTick(this.updateScrollIndicator);
        },
        'playlist.songs'() {
            if (this.isCarousel) this.$nextTick(this.updateScrollIndicator);
        },
    },
    methods: {
        onCarouselScroll() {
            const el = this.$refs.carousel;
            if (!el) return;
            // Dot tracking
            const centerX = el.scrollLeft + el.clientWidth / 2;
            const items = [...el.querySelectorAll('.song-block')];
            let closest = 0, closestDist = Infinity;
            items.forEach((item, i) => {
                const dist = Math.abs((item.offsetLeft + item.offsetWidth / 2) - centerX);
                if (dist < closestDist) { closestDist = dist; closest = i; }
            });
            this.activeIndex = closest;
            // Scroll indicator
            this.updateScrollIndicator();
        },
        scrollToSlide(index) {
            const el = this.$refs.carousel;
            if (!el) return;
            const items = [...el.querySelectorAll('.song-block')];
            const target = items[index];
            if (!target) return;
            el.scrollTo({ left: target.offsetLeft, behavior: 'smooth' });
        },
        updateScrollIndicator() {
            const el = this.$refs.carousel;
            if (!el) return;
            const thumbWidth = (el.clientWidth / el.scrollWidth) * 100;
            const maxLeft = 100 - thumbWidth;
            const scrollable = el.scrollWidth - el.clientWidth;
            const thumbLeft = scrollable > 0 ? (el.scrollLeft / scrollable) * maxLeft : 0;
            this.scrollThumbWidth = thumbWidth;
            this.scrollThumbLeft = thumbLeft;
            this.isScrollable = scrollable > 0;
        },
    },
}
</script>

<style lang="scss" scoped>
@import "@/scss/_variables.scss";

#songs-playlist-profile {
    width: 100%;
}

.carousel-outer {
    width: 100%;
    overflow: hidden;
}

.carousel {
    display: flex;
    overflow-x: auto;
    scroll-snap-type: x mandatory;
    -webkit-overflow-scrolling: touch;
    scrollbar-width: none;
    &::-webkit-scrollbar { display: none; }

    @media #{$large-screen} {
        gap: $mg-desktop;
        // padding: 0 $mg-desktop;
        // scroll-padding: 0 $mg-desktop;
        padding: 0 0 0 $mg-desktop;
        scroll-padding: 0 0 0 $mg-desktop;
        justify-content: safe center;

        &::after {
            content: '';
            flex-shrink: 0;
            // width: $mg-desktop;
            width: 0;
        }

        ::v-deep .song-block {
            scroll-snap-align: start;
            flex-shrink: 0;
            margin-bottom: 0;
        }
    }

    @media #{$mobile} {
        gap: $mg-desktop;
        padding: 0 $mg-desktop;
        scroll-padding: 0 $mg-desktop;

        ::v-deep .song-block {
            scroll-snap-align: center;
            flex-shrink: 0;
            margin-bottom: 0;
            width: 100%;
            box-sizing: border-box;
        }
    }
}

.scroll-track {
    position: relative;
    height: 1px;
    background: rgba(255, 255, 255, 0.1);
    max-width: 400px;
    margin: $mg-desktop-lg auto $mg-desktop;

    @media #{$mobile} { display: none; }
}

.scroll-thumb {
    position: absolute;
    top: 0;
    height: 100%;
    background: rgba(255, 255, 255, 0.5);
    transition: left 0.08s linear, width 0.08s linear;
}

.dots {
    display: flex;
    align-items: center;
    width: 100%;
    padding: $mg-desktop-lg 0;

    @media #{$large-screen} { display: none; }
}

.dots-inner {
    flex: 1;
    display: flex;
    justify-content: center;
    align-items: center;
    gap: $mg-desktop;
}

.dot-nav {
    flex-shrink: 0;
    background: none;
    border: none;
    color: $white-sec;
    cursor: pointer;
    padding: $mg-desktop;
    display: flex;
    align-items: center;

    &:disabled { opacity: 0.2; cursor: default; }
}

.flip-h {
    transform: scaleX(-1);
}

.dot {
    width: 3px;
    height: 3px;
    border-radius: 50%;
    background-color: rgba(255, 255, 255, 0.3);
    transition: transform 0.25s ease, background-color 0.2s ease;
    flex-shrink: 0;

    &--active {
        background-color: rgba(255, 255, 255, 0.9);
        transform: scale(2);
    }
}
</style>
